/* ==========================================================================
   Roots header (header-roots)
   Standalone. Registers a custom element <roots-header>. Does not touch,
   extend or depend on any existing theme script.

   Reads its configuration from data attributes written by
   sections/header-roots.liquid, so everything stays editable in the
   theme editor with no code changes.
   Built by theBlur for Roots Rwanda.
   ========================================================================== */

(function () {
  'use strict';

  if (customElements.get('roots-header')) return;

  var SCROLL_THRESHOLD = 40;
  var HIDE_AFTER = 90;
  // Scroll settle and momentum fire events where y barely moves, or does not move
  // at all. Comparing y > lastY on those reads as "scrolling up" and pops the rail
  // back open at the end of every swipe. Only act on a deliberate movement.
  var DIR_THRESHOLD = 5;

  class RootsHeader extends HTMLElement {
    connectedCallback() {
      this.lastY = 0;
      this.ticking = false;
      this.inEditor = document.documentElement.classList.contains('shopify-design-mode') ||
        (window.Shopify && window.Shopify.designMode);

      this.behaviourDesktop = this.dataset.behaviourDesktop || 'compact';
      this.behaviourMobile = this.dataset.behaviourMobile || 'rail_hide';
      this.mq = window.matchMedia('(min-width: 1025px)');

      this.measure();
      this.initScroll();
      this.initSearch();
      this.initVoice();
      this.initPlaceholderRotator();
      this.markActiveDepartment();
      this.watchCart();
      this.initRailNudge();
      this.initShopAll();
    }

    /* ---------------- mobile top row height ----------------
       The collapse animates max-height, which needs the row's real height. That
       height depends on merchant settings and on the aspect ratio of whatever
       logo they upload, so it cannot be hard-coded. Measure it instead. */

    measureMTop() {
      var row = this.querySelector('.rh__m-top');
      if (!row) return;

      var remeasure = () => {
        // measure uncollapsed, then restore whatever state we were in
        var wasScrolled = this.classList.contains('rh-is-scrolled');
        if (wasScrolled) this.classList.remove('rh-is-scrolled');
        var prev = row.style.transition;
        row.style.transition = 'none';
        row.style.maxHeight = 'none';
        var h = row.scrollHeight;
        row.style.maxHeight = '';
        if (h > 0) this.style.setProperty('--rh-m-top-h', h + 'px');
        // force reflow before re-enabling the transition, so nothing animates
        void row.offsetHeight;
        row.style.transition = prev;
        if (wasScrolled) this.classList.add('rh-is-scrolled');
      };

      // Runs the measurement in an idle slot rather than wherever it happened
      // to be triggered from. fonts.ready and image 'load' fire once, at an
      // unpredictable time, and can land in the middle of an active scroll;
      // forcing the reflow above right then produces the same kind of
      // mid-scroll stutter lockFlow used to. requestIdleCallback lets the
      // browser pick a moment it isn't already busy.
      var scheduleRemeasure = () => {
        if (window.requestIdleCallback) {
          requestIdleCallback(remeasure, { timeout: 500 });
        } else {
          setTimeout(remeasure, 0);
        }
      };

      this.remeasureMTop = scheduleRemeasure;
      remeasure(); // run once immediately, before first paint matters most

      // logos and icons can change the height once they decode
      this.querySelectorAll('.rh__m-top img').forEach((img) => {
        if (!img.complete) img.addEventListener('load', scheduleRemeasure, { once: true });
      });
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(scheduleRemeasure);
    }

    /* ---------------- combined measure pass ----------------
       lockFlow() used to live here. It set --rh-full-h and toggled
       .rh-flow-locked on the wrapper, but that CSS was removed, so this was
       forcing two synchronous layout reads and four class toggles on every
       resize, every section edit, and every image load, for a wrapper class
       that no longer did anything. Removed entirely.

       Mobile browsers also fire `resize` when the address bar shows or hides
       during a scroll gesture. That changes viewport HEIGHT, not width.
       Reacting to it forced a remeasure at exactly the moment a scroll frame
       was already busy, which is what produced the mid-scroll stutter. Only
       remeasure on a real width change now. */

    measure() {
      this.measureMTop();

      var lastWidth = window.innerWidth;
      var t = null;
      window.addEventListener('resize', () => {
        if (window.innerWidth === lastWidth) return;
        lastWidth = window.innerWidth;
        clearTimeout(t);
        t = setTimeout(() => { if (this.remeasureMTop) this.remeasureMTop(); }, 150);
      }, { passive: true });

      // the theme editor re-renders settings without a reload
      document.addEventListener('shopify:section:load', () => {
        if (this.remeasureMTop) this.remeasureMTop();
      });
      document.addEventListener('shopify:section:select', () => {
        if (this.remeasureMTop) this.remeasureMTop();
      });
    }

    /* ---------------- scroll behaviour ---------------- */

    behaviour() {
      return this.mq.matches ? this.behaviourDesktop : this.behaviourMobile;
    }

    initScroll() {
      if (this.dataset.sticky !== 'true') return;

      this.onScroll = () => {
        if (this.ticking) return;
        this.ticking = true;
        window.requestAnimationFrame(() => {
          this.applyScrollState();
          this.ticking = false;
        });
      };

      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.applyScrollState();
    }

    applyScrollState() {
      var mode = this.behaviour();
      var y = window.scrollY || window.pageYOffset;
      if (y < 0) y = 0; // iOS rubber-band

      if (mode === 'none' || this.inEditor) {
        this.classList.remove('rh-is-scrolled', 'rh-rail-collapsed');
        this.lastY = y;
        return;
      }

      this.classList.toggle('rh-is-scrolled', y > SCROLL_THRESHOLD);

      if (mode === 'rail_hide') {
        var delta = y - this.lastY;

        if (y <= HIDE_AFTER) {
          // near the top of the page the rail is always open
          this.classList.remove('rh-rail-collapsed');
          this.lastY = y;
          return;
        }

        if (Math.abs(delta) < DIR_THRESHOLD) return; // settle or jitter, hold state
        this.classList.toggle('rh-rail-collapsed', delta > 0);
        this.lastY = y;
        return;
      }

      this.classList.remove('rh-rail-collapsed');
      this.lastY = y;
    }

    /* ---------------- predictive search ---------------- */

    initSearch() {
      this.searchRoots = this.querySelectorAll('[data-rh-search]');
      if (!this.searchRoots.length) return;
      if (this.dataset.predictive !== 'true') return;

      this.searchRoots.forEach((root) => {
        var input = root.querySelector('[data-rh-search-input]');
        var panel = root.querySelector('[data-rh-predictive]');
        if (!input || !panel) return;

        var form = input.closest('form');

        // Sync immediately for a value that's already there on page load —
        // e.g. Shopify prefills value="{{ search.terms }}" on the search
        // results page itself. That prefill never fires an 'input' event, so
        // without this the rotating placeholder stayed visible, drawn right
        // on top of the real text, on any page that loads with a pre-filled
        // search box.
        if (input.value.trim().length > 0) {
          root.classList.add('rh__search--has-value');
        }

        // Record the term into the shopper's recent searches when they submit,
        // so it shows next time they open the box. Runs before navigation.
        if (form) {
          form.addEventListener('submit', () => {
            this.recordRecentSearch(input.value);
          });
        }

        var timer = null;

        input.addEventListener('input', () => {
          root.classList.toggle('rh__search--has-value', input.value.length > 0);
          clearTimeout(timer);
          var q = input.value.trim();
          if (q.length < 2) {
            // Below the live-search threshold. Instead of an empty panel, show
            // the trending / recent empty state so the box is never blank.
            this.showEmptyState(panel);
            return;
          }
          timer = setTimeout(() => this.fetchPredictive(q, panel), 220);
        });

        input.addEventListener('focus', () => {
          var q = input.value.trim();
          if (q.length >= 2 && panel.innerHTML.trim() && !panel.dataset.rhEmptyState) {
            panel.classList.add('rh-is-open');
          } else {
            // Empty or too-short box: show trending + recent.
            this.showEmptyState(panel);
          }
        });

        input.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            this.closePanel(panel);
            input.blur();
          }
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            var items = panel.querySelectorAll('[data-rh-predictive-item], .rh__predictive-item');
            if (!items.length) return;
            e.preventDefault();
            var current = panel.querySelector('.rh-is-cursor');
            var idx = current ? Array.prototype.indexOf.call(items, current) : -1;
            idx = e.key === 'ArrowDown' ? Math.min(idx + 1, items.length - 1) : Math.max(idx - 1, 0);
            items.forEach((i) => i.classList.remove('rh-is-cursor'));
            items[idx].classList.add('rh-is-cursor');
            items[idx].scrollIntoView({ block: 'nearest' });
          }
          if (e.key === 'Enter') {
            var cursor = panel.querySelector('.rh-is-cursor');
            if (cursor) {
              e.preventDefault();
              window.location.href = cursor.href;
            }
          }
        });

        document.addEventListener('click', (e) => {
          if (!root.contains(e.target)) this.closePanel(panel);
        });
      });
    }

    closePanel(panel) {
      panel.classList.remove('rh-is-open');
    }

    fetchPredictive(q, panel) {
      /* --------------------------------------------------------------------
         Ask Shopify to render the predictive-results section server-side for
         this query, then swap the returned HTML into the panel. Two reasons
         to do it this way rather than fetch /search/suggest.json:

         1. `fields` widening — the JSON endpoint only searches the default
            field set (title, product_type, tags). The section-render request
            below adds `variants.title` and `variants.sku` and `vendor`, so
            typing an SKU or a variant name actually returns the product. On
            a 3000-SKU catalog with lots of Samsung/Hisense model numbers,
            this is the difference between "nothing found" and the right hit.

         2. `resources[type]` widening — we ask for query suggestions,
            products, collections, pages AND articles. The old JSON version
            only did query + collection + product. Now shipping pages
            (Delivery Info, MoMo help) and blog articles too.

         The response is a full HTML document containing the rendered section.
         Parse it, pull out the <div class="rhp">, replace the panel's guts.
         -------------------------------------------------------------------- */

      var sectionId = this.dataset.predictiveSection || 'rh-predictive-results';
      var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';

      /* Must hit the PREDICTIVE endpoint (/search/suggest), not /search.
         The section reads the Liquid `predictive_search` object, which Shopify
         only populates on the predictive endpoint. Rendering a section off the
         plain /search route leaves predictive_search empty, so every query
         falls through to "no matches" even when the product exists. */
      var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.predictive_search_url)
        ? window.Shopify.routes.predictive_search_url
        : root + 'search/suggest';

      var params = [
        'q=' + encodeURIComponent(q),
        'resources[type]=query,product,collection,page,article',
        'resources[options][unavailable_products]=last',
        'resources[options][prefix]=last',
        'resources[options][fields]=title,product_type,vendor,tag,variants.title,variants.sku',
        'section_id=' + encodeURIComponent(sectionId)
      ].join('&');

      // Abort any older, still-inflight fetch so a slow response for "sam"
      // cannot arrive after "samsung" and overwrite the newer results.
      if (this._searchAbort) this._searchAbort.abort();
      var ctrl = ('AbortController' in window) ? new AbortController() : null;
      this._searchAbort = ctrl;

      panel.classList.add('rh-is-loading');

      fetch(base + '?' + params, ctrl ? { signal: ctrl.signal } : {})
        .then((r) => {
          if (!r.ok) throw new Error('search failed: ' + r.status);
          return r.text();
        })
        .then((text) => {
          // Shopify wraps the section HTML in <div id="shopify-section-<id>">.
          // Parse defensively; on some themes the wrapper is absent.
          var doc = new DOMParser().parseFromString(text, 'text/html');
          var wrap = doc.getElementById('shopify-section-' + sectionId);
          var body = wrap ? wrap.querySelector('[data-rh-predictive-body]') : doc.querySelector('[data-rh-predictive-body]');

          panel.classList.remove('rh-is-loading');
          if (!body) {
            this.renderPredictiveFallback(panel, q);
            return;
          }
          delete panel.dataset.rhEmptyState;
          panel.innerHTML = body.outerHTML;
          panel.classList.add('rh-is-open');
        })
        .catch((err) => {
          if (err && err.name === 'AbortError') return; // superseded by a newer keystroke, fine
          panel.classList.remove('rh-is-loading');
          this.renderPredictiveFallback(panel, q);
        });
    }

    renderPredictiveFallback(panel, q) {
      /* If the section is missing (e.g. merchant hasn't added the file yet) or
         Shopify returned unexpected markup, fall back to a minimal "no matches"
         message rather than leaving the previous results stale. This is the
         one place a raw string is safe: `q` is inserted via textContent. */
      var empty = document.createElement('p');
      empty.className = 'rh__predictive-empty';
      empty.textContent = (this.dataset.labelNoResults || 'No results for “[q]”').replace('[q]', q);
      panel.innerHTML = '';
      panel.appendChild(empty);
      panel.classList.add('rh-is-open');
    }

    /* ---------------- trending + recent empty state ---------------- */

    recentKey() {
      return 'rh_recent_searches';
    }

    getRecentSearches() {
      if (this.dataset.recentEnabled !== 'true') return [];
      try {
        var raw = window.localStorage.getItem(this.recentKey());
        var arr = raw ? JSON.parse(raw) : [];
        return Array.isArray(arr) ? arr : [];
      } catch (e) {
        // localStorage can throw in private mode or when disabled. Fail quiet.
        return [];
      }
    }

    recordRecentSearch(term) {
      if (this.dataset.recentEnabled !== 'true') return;
      term = (term || '').trim();
      if (term.length < 2) return;
      var limit = parseInt(this.dataset.recentLimit, 10) || 5;
      try {
        var list = this.getRecentSearches();
        // De-dupe case-insensitively, newest first.
        var lower = term.toLowerCase();
        list = list.filter(function (t) { return t.toLowerCase() !== lower; });
        list.unshift(term);
        list = list.slice(0, limit);
        window.localStorage.setItem(this.recentKey(), JSON.stringify(list));
      } catch (e) { /* storage unavailable, ignore */ }
    }

    clearRecentSearches() {
      try { window.localStorage.removeItem(this.recentKey()); } catch (e) {}
    }

    getTrending() {
      try {
        var raw = this.dataset.trending;
        var arr = raw ? JSON.parse(raw) : [];
        return Array.isArray(arr) ? arr : [];
      } catch (e) {
        return [];
      }
    }

    showEmptyState(panel) {
      var trending = this.getTrending();
      var recent = this.getRecentSearches();

      // Nothing to show at all: keep the panel closed rather than showing an
      // empty white box.
      if (!trending.length && !recent.length) {
        this.closePanel(panel);
        return;
      }

      var searchUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root)
        ? window.Shopify.routes.root + 'search'
        : '/search';

      var frag = document.createDocumentFragment();
      var wrap = document.createElement('div');
      wrap.className = 'rhp rhp--empty-state';

      // Recent block first (most personal / most likely to be reused).
      if (recent.length) {
        var rGroup = document.createElement('div');
        rGroup.className = 'rhp__group rhp__group--pills';

        var rHead = document.createElement('div');
        rHead.className = 'rhp__group-title';
        rHead.appendChild(document.createTextNode(this.dataset.labelRecent || 'Recent searches'));

        var clear = document.createElement('button');
        clear.type = 'button';
        clear.className = 'rhp__clear-recent';
        clear.textContent = this.dataset.labelClearRecent || 'Clear';
        clear.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.clearRecentSearches();
          this.showEmptyState(panel); // re-render without recent
        });
        rHead.appendChild(clear);
        rGroup.appendChild(rHead);

        var rPills = document.createElement('div');
        rPills.className = 'rhp__pills';
        recent.forEach((term) => {
          var a = document.createElement('a');
          a.className = 'rhp__pill rhp__pill--recent';
          a.href = searchUrl + '?q=' + encodeURIComponent(term);
          a.rel = 'nofollow';
          // small clock glyph + text, text via textContent to stay injection-safe
          var ic = document.createElement('span');
          ic.className = 'rhp__pill-ic';
          ic.setAttribute('aria-hidden', 'true');
          ic.innerHTML = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>';
          a.appendChild(ic);
          a.appendChild(document.createTextNode(term));
          rPills.appendChild(a);
        });
        rGroup.appendChild(rPills);
        wrap.appendChild(rGroup);
      }

      // Trending block.
      if (trending.length) {
        var tGroup = document.createElement('div');
        tGroup.className = 'rhp__group rhp__group--pills';

        var tHead = document.createElement('div');
        tHead.className = 'rhp__group-title';
        tHead.textContent = this.dataset.labelTrending || 'Trending searches';
        tGroup.appendChild(tHead);

        var tPills = document.createElement('div');
        tPills.className = 'rhp__pills';
        trending.forEach((item) => {
          if (!item || !item.label) return;
          var a = document.createElement('a');
          a.className = 'rhp__pill rhp__pill--trending';
          a.href = item.url || (searchUrl + '?q=' + encodeURIComponent(item.label));
          var ic = document.createElement('span');
          ic.className = 'rhp__pill-ic';
          ic.setAttribute('aria-hidden', 'true');
          ic.innerHTML = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 17l6-6 4 4 8-8"/><path d="M17 7h4v4"/></svg>';
          a.appendChild(ic);
          a.appendChild(document.createTextNode(item.label));
          tPills.appendChild(a);
        });
        tGroup.appendChild(tPills);
        wrap.appendChild(tGroup);
      }

      frag.appendChild(wrap);
      panel.innerHTML = '';
      panel.appendChild(frag);
      panel.dataset.rhEmptyState = 'true';
      panel.classList.add('rh-is-open');
    }

    money(cents) {
      var fmt = this.dataset.moneyFormat || '{{amount}}';
      var amount = (parseInt(cents, 10) / 100);
      var rounded = amount % 1 === 0 ? amount.toFixed(0) : amount.toFixed(2);
      var withCommas = rounded.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return fmt.replace(/\{\{\s*amount[^}]*\}\}/, withCommas);
    }

    esc(str) {
      var d = document.createElement('div');
      d.textContent = str == null ? '' : String(str);
      return d.innerHTML;
    }

    /* ---------------- voice search ---------------- */

    initVoice() {
      var SR = window.SpeechRecognition || window.webkitSpeechRecognition;
      var mics = this.querySelectorAll('[data-rh-mic]');
      if (!mics.length) return;

      if (!SR) {
        mics.forEach((m) => m.remove());
        this.querySelectorAll('.rh__search-divider').forEach((d) => d.remove());
        return;
      }

      mics.forEach((mic) => {
        mic.addEventListener('click', () => {
          var form = mic.closest('form');
          var input = form && form.querySelector('[data-rh-search-input]');
          if (!input) return;

          var rec = new SR();
          rec.lang = this.dataset.voiceLang || 'en-US';
          rec.interimResults = false;
          rec.maxAlternatives = 1;

          mic.classList.add('rh-is-listening');

          rec.onresult = (e) => {
            var text = e.results[0][0].transcript;
            input.value = text;
            input.dispatchEvent(new Event('input', { bubbles: true }));
            if (this.dataset.voiceAutoSubmit === 'true') form.submit();
          };
          rec.onend = () => mic.classList.remove('rh-is-listening');
          rec.onerror = () => mic.classList.remove('rh-is-listening');

          try { rec.start(); } catch (err) { mic.classList.remove('rh-is-listening'); }
        });
      });
    }

    /* ---------------- rotating placeholder ---------------- */

    initPlaceholderRotator() {
      this.querySelectorAll('[data-rh-rotator]').forEach((rot) => {
        var terms = (rot.dataset.rhRotator || '').split('|').map((t) => t.trim()).filter(Boolean);
        if (terms.length < 2) return;
        var every = (parseInt(rot.dataset.rhRotatorEvery, 10) || 3) * 1000;
        var span = rot.querySelector('span');
        var i = 0;

        setInterval(() => {
          rot.classList.add('rh-is-out');
          setTimeout(() => {
            i = (i + 1) % terms.length;
            span.textContent = terms[i];
            rot.classList.remove('rh-is-out');
          }, 260);
        }, every);
      });
    }

    /* ---------------- active department ---------------- */

    /* ---------------- active department ----------------
       Desktop and mobile each render their own copy of the department rail
       from the same blocks, so a department with one link produces two DOM
       elements with an identical href, one hidden by the responsive media
       query rather than absent. The old version kept a single "best" match
       and only replaced it on a strictly-longer href, so the two copies tied
       and whichever came first in DOM order, desktop, won every time. The
       mobile copy matched the same URL and never got marked. Two passes: find
       the best length once, then mark every element tied at that length. */

    markActiveDepartment() {
      var path = window.location.pathname;
      var depts = Array.prototype.slice.call(this.querySelectorAll('[data-rh-dept]'));

      function cleanHref(el) {
        var href = el.getAttribute('href') || '';
        if (!href || href === '/' || href === '#') return null;
        var clean = href.split('?')[0].replace(/\/$/, '');
        return clean || null;
      }

      var bestLen = 0;
      depts.forEach((el) => {
        var clean = cleanHref(el);
        if (!clean) return;
        if (path === clean || path.indexOf(clean + '/') === 0) {
          if (clean.length > bestLen) bestLen = clean.length;
        }
      });

      if (bestLen === 0) return;

      depts.forEach((el) => el.classList.remove('rh-is-active'));

      depts.forEach((el) => {
        var clean = cleanHref(el);
        if (!clean || clean.length !== bestLen) return;
        if (path !== clean && path.indexOf(clean + '/') !== 0) return;

        el.classList.add('rh-is-active');
        // keep the active chip in view on its own horizontal rail
        var rail = el.closest('.rh__rail');
        if (rail && rail.scrollWidth > rail.clientWidth) {
          rail.scrollLeft = el.offsetLeft - (rail.clientWidth / 2) + (el.clientWidth / 2);
        }
      });
    }

    /* ---------------- live cart count ---------------- */

    watchCart() {
      document.addEventListener('cart:updated', (e) => this.setCount(e.detail && e.detail.cart));
      document.addEventListener('cart:refresh', () => this.refetchCart());
      // Theme-agnostic fallback: re-read the cart after any add-to-cart POST.
      var origFetch = window.fetch;
      var self = this;
      window.fetch = function () {
        var args = arguments;
        return origFetch.apply(this, args).then(function (res) {
          try {
            var url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
            if (/\/cart\/(add|change|update|clear)/.test(url)) setTimeout(() => self.refetchCart(), 60);
          } catch (err) { /* no-op */ }
          return res;
        });
      };
    }

    refetchCart() {
      fetch((window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/') + 'cart.js')
        .then((r) => r.json())
        .then((cart) => this.setCount(cart))
        .catch(() => {});
    }

    setCount(cart) {
      if (!cart) return;
      var n = cart.item_count;
      this.querySelectorAll('[data-rh-cart-count]').forEach((el) => {
        el.textContent = n > 99 ? '99+' : n;
        el.hidden = n === 0;
      });
    }

    /* ---------------- one-time rail nudge ---------------- */

    initRailNudge() {
      var rail = this.querySelector('.rh__mobile .rh__rail');
      if (!rail) return;

      var KEY = 'rh_rail_nudge_seen';
      var seen = false;
      try { seen = sessionStorage.getItem(KEY) === '1'; } catch (e) { /* private mode, treat as unseen */ }
      if (seen) return;

      var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduceMotion) {
        try { sessionStorage.setItem(KEY, '1'); } catch (e) { /* ignore */ }
        return;
      }

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          var maxScroll = rail.scrollWidth - rail.clientWidth;
          if (maxScroll <= 2) return;

          var peek = Math.min(64, maxScroll);
          var out = 900;
          var back = 700;
          var hold = 250;
          var start = null;

          var easeOutCubic = function (t) { return 1 - Math.pow(1 - t, 3); };
          var easeInOutCubic = function (t) {
            return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
          };

          function step(ts) {
            if (start === null) start = ts;
            var elapsed = ts - start;

            if (elapsed <= out) {
              rail.scrollLeft = easeOutCubic(elapsed / out) * peek;
              requestAnimationFrame(step);
            } else if (elapsed <= out + hold) {
              rail.scrollLeft = peek;
              requestAnimationFrame(step);
            } else if (elapsed <= out + hold + back) {
              var t = (elapsed - out - hold) / back;
              rail.scrollLeft = peek * (1 - easeInOutCubic(t));
              requestAnimationFrame(step);
            } else {
              rail.scrollLeft = 0;
            }
          }

          setTimeout(() => requestAnimationFrame(step), 500);
          try { sessionStorage.setItem(KEY, '1'); } catch (e) { /* ignore */ }
        });
      });
    }

    /* ---------------- Shop All / rail dropdown ----------------
       Desktop: a department block can carry its own two-level menu. Hover or
       click (merchant's choice) opens a panel below the rail — a left column
       of top-level links and a right pane of that link's children, swapped
       on hover/click/focus of the left column. Only one panel open at a time.
       Mobile: same data, rendered as a full-width accordion under the rail
       instead, since there's no hover and no room for two columns. */

    initShopAll() {
      var self = this;
      var trigger = this.dataset.shopallTrigger || 'hover';
      var closeDelay = parseInt(this.dataset.shopallCloseDelay, 10);
      if (isNaN(closeDelay)) closeDelay = 200;

      var backdrop = this.querySelector('[data-rh-shopall-backdrop]');
      var closeTimer = null;

      /* Buttons and panels are in separate parts of the DOM (the panels had to
         move out of the clipped rail wrapper), so they are paired by block id
         rather than by nesting. */
      var pairs = [];
      Array.prototype.slice.call(this.querySelectorAll('[data-rh-shopall-btn]')).forEach(function (btn) {
        var id = btn.getAttribute('data-rh-shopall-btn');
        var panel = self.querySelector('[data-rh-shopall-panel="' + id + '"]');
        if (panel) pairs.push({ id: id, btn: btn, panel: panel });
      });

      if (!pairs.length) {
        this.initShopAllMobile();
        return;
      }

      function closeAll() {
        pairs.forEach(function (p) {
          p.btn.classList.remove('rh-is-open');
          p.btn.setAttribute('aria-expanded', 'false');
          p.panel.classList.remove('rh-is-open');
        });
        if (backdrop) backdrop.classList.remove('rh-is-open');
      }

      /* The panel lives in a layer detached from the rail, so nothing lines it
         up with its button automatically. Measure the button against the
         layer's own container and offset the panel to match, clamped so a
         button near the right edge does not push the panel off screen. This
         also keeps it correct when the rail is centre-aligned or has been
         scrolled horizontally. */
      function positionPanel(pair) {
        var container = pair.panel.parentElement;
        if (!container) return;

        var containerRect = container.getBoundingClientRect();
        var btnRect = pair.btn.getBoundingClientRect();
        var panelWidth = pair.panel.offsetWidth;

        var gutter = parseFloat(
          getComputedStyle(self).getPropertyValue('--rh-gutter')
        );
        if (isNaN(gutter)) gutter = 28;

        var x = btnRect.left - containerRect.left;
        var maxX = containerRect.width - panelWidth - gutter;
        if (maxX < gutter) maxX = gutter;
        if (x > maxX) x = maxX;
        if (x < gutter) x = gutter;

        pair.panel.style.setProperty('inset-inline-start', x + 'px');
      }

      function open(pair) {
        closeAll();
        positionPanel(pair);
        pair.btn.classList.add('rh-is-open');
        pair.btn.setAttribute('aria-expanded', 'true');
        pair.panel.classList.add('rh-is-open');
        if (trigger === 'click' && backdrop) backdrop.classList.add('rh-is-open');
      }

      window.addEventListener('resize', function () {
        pairs.forEach(function (p) {
          if (p.panel.classList.contains('rh-is-open')) positionPanel(p);
        });
      });

      pairs.forEach(function (pair) {
        var listItems = Array.prototype.slice.call(pair.panel.querySelectorAll('[data-rh-shopall-cat]'));
        var panes = Array.prototype.slice.call(pair.panel.querySelectorAll('[data-rh-shopall-pane]'));

        function activate(key) {
          listItems.forEach(function (i) {
            var on = i.getAttribute('data-rh-shopall-cat') === key;
            i.classList.toggle('is-active', on);
            i.setAttribute('aria-selected', on ? 'true' : 'false');
          });
          panes.forEach(function (pn) {
            pn.classList.toggle('is-active', pn.getAttribute('data-rh-shopall-pane') === key);
          });
        }

        listItems.forEach(function (item) {
          var key = item.getAttribute('data-rh-shopall-cat');
          item.addEventListener('mouseenter', function () { activate(key); });
          item.addEventListener('click', function () { activate(key); });
          item.addEventListener('focus', function () { activate(key); });
        });

        pair.btn.addEventListener('click', function (e) {
          e.preventDefault();
          if (pair.panel.classList.contains('rh-is-open')) {
            closeAll();
          } else {
            open(pair);
          }
        });

        if (trigger === 'hover') {
          pair.btn.addEventListener('mouseenter', function () {
            clearTimeout(closeTimer);
            open(pair);
          });
          pair.btn.addEventListener('mouseleave', function () {
            closeTimer = setTimeout(closeAll, closeDelay);
          });
          pair.panel.addEventListener('mouseenter', function () {
            clearTimeout(closeTimer);
          });
          pair.panel.addEventListener('mouseleave', function () {
            closeTimer = setTimeout(closeAll, closeDelay);
          });
        }

        pair.btn.addEventListener('focus', function () {
          clearTimeout(closeTimer);
          open(pair);
        });
      });

      if (backdrop) backdrop.addEventListener('click', closeAll);

      document.addEventListener('click', function (e) {
        var openPair = null;
        pairs.forEach(function (p) {
          if (p.panel.classList.contains('rh-is-open')) openPair = p;
        });
        if (!openPair) return;
        if (openPair.btn.contains(e.target) || openPair.panel.contains(e.target)) return;
        closeAll();
      });

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
          closeAll();
          self.closeAllMobileShopAll();
        }
      });

      this.initShopAllMobile();
    }

    initShopAllMobile() {
      var self = this;
      var mobileBtns = Array.prototype.slice.call(this.querySelectorAll('[data-rh-shopall-mobile-btn]'));

      mobileBtns.forEach(function (btn) {
        var id = btn.getAttribute('data-rh-shopall-mobile-btn');
        var panel = self.querySelector('[data-rh-shopall-mobile-panel="' + id + '"]');
        if (!panel) return;

        btn.addEventListener('click', function (e) {
          e.preventDefault();
          var isOpen = panel.classList.contains('rh-is-open');
          self.closeAllMobileShopAll();
          if (!isOpen) {
            panel.classList.add('rh-is-open');
            btn.classList.add('rh-is-open');
            btn.setAttribute('aria-expanded', 'true');
          }
        });

        var subToggles = Array.prototype.slice.call(panel.querySelectorAll('[data-rh-m-shopall-toggle]'));
        subToggles.forEach(function (head) {
          var key = head.getAttribute('data-rh-m-shopall-toggle');
          var sub = panel.querySelector('[data-rh-m-shopall-sub="' + key + '"]');
          head.addEventListener('click', function () {
            var wasOpen = sub && sub.classList.contains('rh-is-open');
            subToggles.forEach(function (h) {
              h.setAttribute('aria-expanded', 'false');
              var s = panel.querySelector('[data-rh-m-shopall-sub="' + h.getAttribute('data-rh-m-shopall-toggle') + '"]');
              if (s) s.classList.remove('rh-is-open');
            });
            if (sub && !wasOpen) {
              sub.classList.add('rh-is-open');
              head.setAttribute('aria-expanded', 'true');
            }
          });
        });
      });
    }

    closeAllMobileShopAll() {
      this.querySelectorAll('[data-rh-shopall-mobile-panel]').forEach(function (p) {
        p.classList.remove('rh-is-open');
      });
      this.querySelectorAll('[data-rh-shopall-mobile-btn]').forEach(function (b) {
        b.classList.remove('rh-is-open');
        b.setAttribute('aria-expanded', 'false');
      });
    }

    disconnectedCallback() {
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    }
  }

  customElements.define('roots-header', RootsHeader);
})();
